import type { IPropertyBag } from '@deity-io/di-data-basics';
import { normalizeKey } from '@deity-io/di-data-basics';
import type { IMappingContainer } from '@deity-io/di-data-mappings';
import { minimizeTargetSettings, normalizeTargetSettings } from '@deity-io/di-data-mappings';
import {
  DefinitionLoader,
  MappingLoader,
  StructureEditor,
  validatePropertyList
} from '@deity-io/di-data-structure-editor';
import type { DictMapping, FieldMapping, Mapping } from '@deity-io/di-data-structure-editor';
import { PathItem } from '@deity-io/di-data-structure-editor/dist/editor/pathItems';
import { buildPath } from 'components/MappingEditor/helpers/mappingHelpers';
import type { ICollectionType } from 'contracts/interfaces';
import _, { cloneDeep } from 'lodash';
import { v4 } from 'uuid';
import type {
  IConnectPayload,
  ICreateSourceProcessor,
  IMappingState,
  INewTargetProperty,
  IRemoveTargetProperty,
  ISelectedPayload,
  IUpdateDefault,
  IUpdateTargetProperty,
  InitMappingSchema,
  MappingActions
} from './MappingContext';
import { MappingActionTypes } from './MappingContext';

export const mappingReducer = (draftState: IMappingState, action: MappingActions) => {
  switch (action.type) {
    case MappingActionTypes.INIT_MAPPING:
      return initMapping(draftState, action);
    case MappingActionTypes.CONNECT: {
      if (draftState.locked) {
        return draftState;
      }

      return toggleConnection(draftState, action.payload, true);
    }
    case MappingActionTypes.DISCONNECT:
      if (draftState.locked) {
        return draftState;
      }

      return toggleConnection(draftState, action.payload, false);
    case MappingActionTypes.UPDATE_DEFAULT:
      if (draftState.locked) {
        return draftState;
      }

      return updateDefaultSetting(draftState, action.payload);
    case MappingActionTypes.UPDATE_TARGET_PROPERTY:
      if (draftState.locked) {
        return draftState;
      }

      return updateTargetProperty(draftState, action.payload);
    case MappingActionTypes.NEW_TARGET_PROPERTY:
      if (draftState.locked) {
        return draftState;
      }

      return newTargetProperty(draftState, action.payload);
    case MappingActionTypes.REMOVE_TARGET_PROPERTY:
      if (draftState.locked) {
        return draftState;
      }

      return removeTargetProperty(draftState, action.payload);
    case MappingActionTypes.CREATE_SOURCE_MAPPING:
      if (draftState.locked) {
        return draftState;
      }

      return createDanglingMapping(draftState as IMappingState, action.payload, true);
    case MappingActionTypes.CREATE_OTHER_SOURCE_MAPPING:
      if (draftState.locked) {
        return draftState;
      }

      return createOtherMapping(draftState, action.payload);
    case MappingActionTypes.CREATE_TARGET_MAPPING:
      if (draftState.locked) {
        return draftState;
      }

      return createDanglingMapping(draftState as IMappingState, action.payload, false);
    case MappingActionTypes.MAPPING_DISCONNECT_SOURCE:
    case MappingActionTypes.MAPPING_DISCONNECT_TARGET:
      if (draftState.locked) {
        return draftState;
      }

      return disconnectMapping(
        draftState as IMappingState,
        action.payload,
        action.type === MappingActionTypes.MAPPING_DISCONNECT_SOURCE
      );
    case MappingActionTypes.MAPPING_REMOVE_SOURCE:
      if (draftState.locked) {
        return draftState;
      }

      return removeMapping(draftState as IMappingState, action.payload, true);
    case MappingActionTypes.MAPPING_REMOVE_TARGET:
      if (draftState.locked) {
        return draftState;
      }

      return removeMapping(draftState as IMappingState, action.payload, false);
    case MappingActionTypes.SELECT:
      return updateSelected(draftState, action.payload);
    case MappingActionTypes.CREATE_SOURCE_PROCESSOR:
      return createSourceProcessor(draftState, action);
    case MappingActionTypes.CLEAR:
      return {
        locked: true,
        selected: undefined,
        sourceSchema: null,
        targetSchema: null,
        mappingSchema: null,
        mappingSummary: null,
        structureHashId: '',
        validationMessages: {}
      };
    default:
      return draftState;
  }
};

const cloneEditor = (editor: StructureEditor): StructureEditor => {
  // cloneDeep gives me problems with the itemLists. items keep disappearing
  const clonedEditor = new StructureEditor();
  // clonedEditor.setMapping(cloneDeep(editor.getMapping()));
  // clonedEditor.setSourceSchema(cloneDeep(editor.getSourceSchema()));
  // clonedEditor.setTargetSchema(cloneDeep(editor.getTargetSchema()));
  // clonedEditor.init();
  const oldMapping = editor.getMapping();
  const clonedMapping = MappingLoader.load<IMappingContainer>(oldMapping.key, oldMapping.toJSON());
  if (clonedMapping) {
    clonedEditor.setMapping(clonedMapping);
  } else {
    clonedEditor.setMapping(oldMapping);
    // eslint-disable-next-line no-console
    console.warn('Warning using unsafe mapping, should not happen');
  }
  clonedEditor.setSourceSchema(editor.getSourceSchema());
  clonedEditor.setTargetSchema(editor.getTargetSchema());
  clonedEditor.init();

  return clonedEditor;
};

const initMapping = (draftState: IMappingState, message: InitMappingSchema): IMappingState => {
  const addErrors = (
    validation: IPropertyBag,
    mappingPath: Array<string>,
    info: Array<{ path: string; message: string }>
  ) => {
    const pathItem = PathItem.formArray(mappingPath);
    info.forEach(itemValidation => {
      if (!(itemValidation.path in validation)) {
        validation[pathItem.string] = [];
      }
      // eslint-disable-next-line no-plusplus
      errorCount++;
      validation[pathItem.string].push(itemValidation.message);
    });
  };

  const editor = loadEditor(
    normalizeKey(message.payload.mapping.name),
    _.cloneDeep(message.payload.mapping.schema),
    message.payload.source.schema,
    message.payload.target.schema
  );

  const validation: IPropertyBag = editor.getValidationMessages();
  const edgeValidation: IPropertyBag = {};
  const mapping = editor.getMapping();
  let errorCount = 0;
  mapping.items.filter((item, path) => {
    const itemProperties = (item as any as FieldMapping).getProperties();

    if (itemProperties) {
      // todo, now source and target are not validated !
      const [valid, itemValidations] = validatePropertyList(
        item.config,
        itemProperties,
        path,
        ['hidden'],
        undefined,
        item
      );
      if (!valid) {
        addErrors(validation, path, itemValidations);
      }
    }
    if ('processors' in item.config) {
      item.config.processors?.forEach((processorConfig, index) => {
        const processorConfigType = typeof processorConfig === 'string' ? processorConfig : processorConfig.type;
        if (processorConfigType) {
          const processorProperties = (item as any as DictMapping).getProcessorProperties(processorConfigType);
          const [valid, itemValidations] = validatePropertyList(
            typeof processorConfig === 'string' ? {} : processorConfig,
            processorProperties,
            [...path, 'processors', index.toString()],
            ['hidden'],
            undefined,
            item
          );
          if (!valid) {
            addErrors(validation, [...path /* , 'processors', index */], itemValidations);
          }
        }
      });
    }
    if ('validators' in item.config) {
      item.config.validators.forEach((processorConfig, index) => {
        if (processorConfig.type) {
          const processorProperties = (item as any as DictMapping).getValidatorProperties(processorConfig.type);
          const [valid, itemValidations] = validatePropertyList(
            processorConfig,
            processorProperties,
            [...path, 'validators', index],
            ['hidden'],
            undefined,
            item
          );
          if (!valid) {
            addErrors(validation, [...path /* , 'validators', index */], itemValidations);
          }
        }
      });
    }

    return undefined;
  });

  return {
    ...draftState,
    structureHashId: v4(),
    selected: undefined,
    mappingSchema: message.payload.mapping.schema,
    uuid: message.payload.mapping.uuid,
    mappingEntity: {
      uuid: message.payload.mapping.uuid,
      packageVersionUuid: message.payload.mapping.packageVersionUuid,
      name: message.payload.mapping.name,
      description: message.payload.mapping.description
    },
    sourceSchema: message.payload.source.schema,
    sourceEntity: {
      uuid: message.payload.source.uuid,
      packageVersionUuid: message.payload.source.packageVersionUuid,
      name: message.payload.source.name,
      description: message.payload.source.description
    },
    targetSchema: message.payload.target.schema,
    targetEntity: {
      uuid: message.payload.target.uuid,
      packageVersionUuid: message.payload.target.packageVersionUuid,
      name: message.payload.target.name,
      description: message.payload.target.description
    },
    structureEditor: editor,
    validationMessages: validation,
    locked: message.payload.locked !== false
  };
};

const updateSelected = (draftState: IMappingState, message?: ISelectedPayload): IMappingState => {
  if (!_.isEqual(message, draftState.selected)) {
    return { ...draftState, selected: message };
  }

  return draftState;
};

const loadEditor = (mappingName, mappingRef, sourceSchemaRef, targetSchemaRef): StructureEditor => {
  const editor = new StructureEditor();
  const mapping = MappingLoader.load(mappingName, mappingRef) as Mapping;

  const sourceSchema = DefinitionLoader.load(mapping.key, sourceSchemaRef);
  const targetSchema = DefinitionLoader.load(mapping.key, targetSchemaRef);

  if (mapping && sourceSchema && targetSchema) {
    editor.setMapping(mapping);
    editor.setSourceSchema(sourceSchema);
    editor.setTargetSchema(targetSchema);
    editor.init();
  }

  return editor;
};

const createSourceProcessor = (state: IMappingState, msg: ICreateSourceProcessor) => {
  const { structureEditor: oldEditor, ...newState } = state;
  if (oldEditor) {
    const editor = cloneEditor(oldEditor);
    const [valid, messages] = editor.createProcessorMapping(
      msg.payload.path,
      msg.payload.type,
      msg.payload.expression,
      msg.payload.fields
    );
    if (valid) {
      return {
        ...newState,
        structureHashId: v4(),
        structureEditor: editor,
        mappingSchema: editor.getMapping().toJSON() as ICollectionType
      };
    }
  }

  return state;
};

const toggleConnection = (state: IMappingState, payload: IConnectPayload, connect: boolean) => {
  const { edge } = payload;
  const { structureEditor: editor, ...newState } = state;

  const source = buildPath(edge.sourcePath);
  const target = buildPath(edge.targetPath);
  if (editor) {
    // const editor = cloneEditor(oldEditor);
    const [valid, message] = connect ? editor.connect(source, target) : editor.disconnect(source);
    if (!valid) {
      // eslint-disable-next-line no-console
      console.warn('Unable to (dis)connect', message);
    }
    const newJSON = editor.getMapping().toJSON();

    return {
      ...newState,
      selected: undefined,
      structureHashId: v4(),
      structureEditor: editor,
      mappingSchema: newJSON as ICollectionType
    };
  }

  return state;
};

const createDanglingMapping = (state: IMappingState, payload: { path: string[] }, isSource: boolean) => {
  const { path } = payload;
  // const { mappingSchema, sourceSchema, targetSchema } = state;
  if (state.structureEditor) {
    const editor = cloneEditor(state.structureEditor);
    if (isSource) {
      editor.createSourceMapping(buildPath(path));
    } else {
      editor.createTargetMapping(buildPath(path));
    }

    return {
      ...state,
      structureHashId: v4(),
      structureEditor: editor,
      mappingSchema: editor.getMapping().toJSON() as ICollectionType
    };
  }

  return state;
};

const createOtherMapping = (state: IMappingState, payload: { path: string[] }) => {
  const { path } = payload;
  if (state.structureEditor) {
    const editor = cloneEditor(state.structureEditor);
    editor.createOtherSourceMapping(buildPath(path));

    return {
      ...state,
      structureHashId: v4(),
      structureEditor: editor,
      mappingSchema: editor.getMapping().toJSON() as ICollectionType
    };
  }

  return state;
};

const disconnectMapping = (state: IMappingState, payload: { path: string[] }, isSource: boolean) => {
  const { path } = payload;
  if (state.structureEditor) {
    const editor = cloneEditor(state.structureEditor);
    if (isSource) {
      editor.disconnectSource(buildPath(path));
    } else {
      editor.disconnectTarget(buildPath(path));
    }

    return {
      ...state,
      structureHashId: v4(),
      structureEditor: editor,
      // selected: undefined, cant do this, then ui isn't updated
      mappingSchema: editor.getMapping().toJSON() as ICollectionType
    };
  }

  return state;
};

const removeMapping = (state: IMappingState, payload: { path: string[] }, isSource: boolean) => {
  const { path } = payload;
  const { structureEditor, mappingSchema, ...restState } = state;
  if (structureEditor) {
    const editor = cloneEditor(structureEditor);
    const [removed, messages] = isSource
      ? editor.removeBySource(buildPath(path))
      : editor.removeByTarget(buildPath(path));

    const mapping = editor.getMapping().toJSON();
    if (!removed) {
      // eslint-disable-next-line no-console
      console.warn('Unable to remove ', messages);
    }

    return {
      ...restState,
      structureEditor: editor,
      structureHashId: v4(),
      selected: undefined,
      mappingSchema: mapping as ICollectionType
    };
  }
  // eslint-disable-next-line no-console
  console.warn('Unable to remove, Structure Editor not set?');

  return state;
};

const updateDefaultSetting = (state: IMappingState, payload: IUpdateDefault['payload']) => {
  const editor = cloneEditor(state.structureEditor!);
  const mapping = editor?.getMapping();
  if (mapping) {
    const mappingItem = mapping.items.get(payload.path);
    if (Array.isArray(mappingItem) || !mappingItem) {
      return state;
    }

    const { config } = mappingItem;
    const oldValue = _.get(config, payload.property);
    if (!_.isEqual(oldValue, payload.value) && mappingItem) {
      const newConfig = Array.isArray(mappingItem) ? cloneDeep(mappingItem[0].config) : cloneDeep(mappingItem.config);
      if (payload.value === undefined) {
        const parent = payload.property.length > 1 ? _.get(newConfig, payload.property.slice(0, -1)) : newConfig;
        const childKey = [...payload.property].pop() as string;
        if (Array.isArray(parent)) {
          const newParent = parent.splice(parseInt(childKey, 10), 1);
        } else {
          delete parent[childKey];
        }
      } else {
        _.set(newConfig, payload.property, payload.value);
      }
      (mappingItem as any)._config = newConfig;
      const newState = { ...state, structureEditor: editor, mappingSchema: mapping.toJSON() as ICollectionType };
      if (payload.regenerateHash === true) {
        newState.structureHashId = v4();
      }

      return newState;
    }
  }

  return state;
};

const removeTargetProperty = (state: IMappingState, payload: IRemoveTargetProperty['payload']) => {
  const editor = cloneEditor(state.structureEditor!);
  const mapping = editor?.getMapping();
  if (mapping) {
    const mappingItem = mapping.items.get(payload.path);
    if (Array.isArray(mappingItem) || !mappingItem) {
      return state;
    }

    const targets = normalizeTargetSettings(mappingItem.config);
    if (targets.at(payload.index)) {
      delete targets[payload.index];
    }
    mappingItem.config.target = minimizeTargetSettings(targets.filter(i => i)).target;
    const newState = { ...state, structureEditor: editor, mappingSchema: mapping.toJSON() as ICollectionType };
    if (payload.regenerateHash === true) {
      newState.structureHashId = v4();
    }

    return newState;
  }

  return state;
};

const newTargetProperty = (state: IMappingState, payload: INewTargetProperty['payload']) => {
  const editor = cloneEditor(state.structureEditor!);
  const mapping = editor?.getMapping();
  if (mapping) {
    const mappingItem = mapping.items.get(payload.path);
    if (Array.isArray(mappingItem) || !mappingItem) {
      return state;
    }

    const targets = normalizeTargetSettings(mappingItem.config);
    targets.push({});
    mappingItem.config.target = targets;
    const newState = { ...state, structureEditor: editor, mappingSchema: mapping.toJSON() as ICollectionType };
    if (payload.regenerateHash === true) {
      newState.structureHashId = v4();
    }

    return newState;
  }

  return state;
};

const updateTargetProperty = (state: IMappingState, payload: IUpdateTargetProperty['payload']) => {
  const editor = cloneEditor(state.structureEditor!);
  const mapping = editor?.getMapping();
  if (mapping) {
    const mappingItem = mapping.items.get(payload.path);
    if (Array.isArray(mappingItem) || !mappingItem) {
      return state;
    }

    const { config } = mappingItem;
    const targets = normalizeTargetSettings(mappingItem.config);
    const indexStr = payload.property.shift();
    if (indexStr) {
      const index = Number.parseInt(indexStr, 10);
      let item = targets.at(index);
      if (!item) {
        item = {};
        targets.push(item);
      }
      if (payload.value === undefined) {
        const parent = payload.property.length > 1 ? _.get(item, payload.property.slice(0, -1)) : item;
        const childKey = [...payload.property].pop() as string;
        if (Array.isArray(parent)) {
          const newParent = parent.splice(parseInt(childKey, 10), 1);
          delete newParent[childKey];
        } else {
          delete parent[childKey];
        }
      } else {
        _.set(item, payload.property, payload.value);
      }
      const newSettings = minimizeTargetSettings(targets).target;
      if (!_.isEqual(config.target, newSettings) && mappingItem) {
        mappingItem.config.target = newSettings;
        const newState = { ...state, structureEditor: editor, mappingSchema: mapping.toJSON() as ICollectionType };
        if (payload.regenerateHash === true) {
          newState.structureHashId = v4();
        }

        return newState;
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('no update, index was not a number');

      return state;
    }
  }

  return state;
};
